/**
 * Auth state for the signed-in viewer (self-hosted magic-link email auth).
 *
 * Three return states (matches the playbook):
 *   - { user: null, loading: true }     → still checking /auth/me
 *   - { user: <doc>, loading: false }   → signed in
 *   - { user: null, loading: false }    → anonymous
 *
 * The session token lives in an httpOnly cookie set by the backend at
 * /api/auth/session, so we always send `credentials: "include"` on
 * auth-bearing fetches and never touch the token from JS.
 *
 * Owner auth (`crt_owner_token` in localStorage) is INDEPENDENT — an owner
 * can be signed in via Google for cross-device pref sync, or not. The two
 * never collide; routes that need owner status still use the owner token
 * dependency on the backend.
 */
import { useEffect, useState, useCallback } from "react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export function startEmailSignIn() {
  window.dispatchEvent(new CustomEvent("crt:open-email-signin"));
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (!res.ok) {
        setUser(null);
        setLoading(false);
        return;
      }
      const body = await res.json();
      setUser(body.authenticated ? body : null);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Auth callback announces success via this event so the rest of the
    // app can re-hydrate without a hard reload.
    const onAuthChange = () => refresh();
    window.addEventListener("crt:auth-changed", onAuthChange);
    return () => window.removeEventListener("crt:auth-changed", onAuthChange);
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch (e) { /* noop */ }
    setUser(null);
    window.dispatchEvent(new CustomEvent("crt:auth-changed"));
  }, []);

  return { user, loading, signIn: startEmailSignIn, signOut, refresh };
}
